/**
 * CRM Pipeline View (Kanban Board)
 * Visual representation of lead-to-resident journey
 */

import React, { useState, useEffect } from 'react';
import { 
  UserPlus, Calendar, Phone, Mail, TrendingUp, Filter,
  ArrowRight, CheckCircle, Clock, AlertTriangle
} from 'lucide-react';
import CRMService from '../../services/crm/CRMService';
import { useAppContext } from '../../context/AppContext';

const PipelineView = ({ facilityId = null }) => {
  const { state } = useAppContext();
  const selectedFacility = facilityId || state.selectedFacility;
  
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  useEffect(() => {
    loadPipeline();
  }, [selectedFacility]);

  const loadPipeline = async () => {
    setLoading(true);
    try {
      const result = await CRMService.getPipelineView(selectedFacility);
      if (result.success) {
        setPipeline(result.data);
      }
    } catch (error) {
      console.error('Error loading pipeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (enquiryId, newStatus) => {
    try {
      const result = await CRMService.updateStatus(enquiryId, newStatus);
      if (result.success) {
        await loadPipeline();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!pipeline) {
    return <div className="text-center p-8 text-gray-500">No pipeline data</div>;
  }

  const stages = [
    { key: 'enquiry', label: 'Enquiry', color: 'blue' },
    { key: 'tour', label: 'Tour', color: 'purple' },
    { key: 'application', label: 'Application', color: 'yellow' },
    { key: 'assessment', label: 'Assessment', color: 'orange' },
    { key: 'waitlist', label: 'Waitlist', color: 'gray' },
    { key: 'agreement', label: 'Agreement', color: 'green' },
    { key: 'admission', label: 'Admission', color: 'emerald' }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Conversion Metrics */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="font-semibold text-lg mb-4">Conversion Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-600">Enquiry to Tour</p>
            <p className="text-2xl font-bold text-blue-600">
              {pipeline.conversionRates.enquiryToTour.toFixed(1)}%
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Tour to Application</p>
            <p className="text-2xl font-bold text-purple-600">
              {pipeline.conversionRates.tourToApplication.toFixed(1)}%
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Application to Admission</p>
            <p className="text-2xl font-bold text-green-600">
              {pipeline.conversionRates.applicationToAdmission.toFixed(1)}%
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Overall Conversion</p>
            <p className="text-2xl font-bold text-emerald-600">
              {pipeline.conversionRates.overallConversion.toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map(stage => {
          const enquiries = pipeline.pipeline[stage.key] || [];
          
          return (
            <div
              key={stage.key}
              className="flex-shrink-0 w-80 bg-gray-50 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">{stage.label}</h3>
                <span className="px-2 py-1 text-xs bg-white rounded-full text-gray-600">
                  {enquiries.length}
                </span>
              </div>
              
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {enquiries.map(enquiry => (
                  <div
                    key={enquiry.id}
                    className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedEnquiry(enquiry)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-800">{enquiry.name}</h4>
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(enquiry.priority)}`}>
                        {enquiry.priority}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <TrendingUp className="w-3 h-3" />
                        Score: {enquiry.leadScore}
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-600 mb-2">
                      {enquiry.careLevel || 'Care level not specified'}
                    </p>
                    
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Calendar className="w-3 h-3" />
                      {new Date(enquiry.enquiryDate).toLocaleDateString()}
                    </div>
                  </div>
                ))}
                
                {enquiries.length === 0 && (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    No enquiries in this stage
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Enquiry Detail Modal */}
      {selectedEnquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{selectedEnquiry.name}</h2>
              <button
                onClick={() => setSelectedEnquiry(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <select
                  value={selectedEnquiry.status}
                  onChange={(e) => handleStatusChange(selectedEnquiry.id, e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="tour_scheduled">Tour Scheduled</option>
                  <option value="tour_completed">Tour Completed</option>
                  <option value="application">Application</option>
                  <option value="assessment">Assessment</option>
                  <option value="waitlisted">Waitlisted</option>
                  <option value="agreement">Agreement</option>
                  <option value="admitted">Admitted</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Lead Score</label>
                  <p className="text-lg font-bold text-blue-600">{selectedEnquiry.leadScore}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Priority</label>
                  <p className="text-lg font-semibold">{selectedEnquiry.priority}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Contact</label>
                <p className="text-gray-800">{selectedEnquiry.phone}</p>
                <p className="text-gray-800">{selectedEnquiry.email}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Care Level</label>
                <p className="text-gray-800">{selectedEnquiry.careLevel || 'Not specified'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineView;
